'use client';

import { createUniqueMarkdownHeadingId } from '@/lib/markdown-utils';
import { cn } from '@/lib/utils';
import { shouldRenderMarkdownImageCaption } from '@/lib/markdown-image-caption';
import { normalizeMarkdownMath } from '@/lib/markdown-math';
import { Link2Icon } from 'lucide-react';
import React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import remarkCjkFriendly from 'remark-cjk-friendly';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';

import 'katex/dist/katex.min.css';

import { toast } from 'sonner';

import { CodeBlock } from './code-block';

type HastNode = {
  type?: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

function extractText(node: HastNode | undefined): string {
  if (!node) return '';
  if (typeof node.value === 'string') return node.value;
  if (!node.children?.length) return '';
  return node.children.map(extractText).join('');
}

function isVisuallyEmptyCell(node: HastNode | undefined): boolean {
  const text = extractText(node)
    .replace(/\u00a0/g, ' ')
    .trim();
  return text.length === 0;
}

function mergeEmptyTableCells(tree: HastNode) {
  const visit = (node: HastNode | undefined) => {
    if (!node?.children?.length) return;

    if (node.tagName === 'tbody') {
      const rows = node.children.filter((child) => child.tagName === 'tr');
      const anchors: Array<HastNode | null> = [];

      rows.forEach((row) => {
        if (!row.children?.length) return;

        const nextChildren: HastNode[] = [];
        let columnIndex = 0;
        let encounteredNonEmptyCell = false;

        row.children.forEach((cell) => {
          if (cell.tagName !== 'td' && cell.tagName !== 'th') {
            nextChildren.push(cell);
            return;
          }

          const isEmpty = isVisuallyEmptyCell(cell);

          if (isEmpty && !encounteredNonEmptyCell && anchors[columnIndex]) {
            const anchor = anchors[columnIndex]!;
            const currentRowSpan = Number(anchor.properties?.rowSpan ?? 1);
            anchor.properties = {
              ...(anchor.properties ?? {}),
              rowSpan: currentRowSpan + 1,
            };
          } else {
            nextChildren.push(cell);
            anchors[columnIndex] = cell;
            if (!isEmpty) {
              encounteredNonEmptyCell = true;
            }
          }

          columnIndex += 1;
        });

        row.children = nextChildren;
      });
    }

    node.children.forEach(visit);
  };

  visit(tree);
}

function annotateTables(tree: HastNode) {
  const visit = (node: HastNode | undefined) => {
    if (!node) return;

    if (node.tagName === 'table') {
      const section =
        node.children?.find((child) => child.tagName === 'thead') ??
        node.children?.find((child) => child.tagName === 'tbody');
      const row = section?.children?.find((child) => child.tagName === 'tr');
      const colCount =
        row?.children?.filter(
          (child) => child.tagName === 'td' || child.tagName === 'th',
        ).length ?? 0;

      node.properties = {
        ...(node.properties ?? {}),
        'data-col-count': colCount,
      };
    }

    node.children?.forEach(visit);
  };

  visit(tree);
}

function rehypeMergeEmptyTableCells() {
  return (tree: HastNode) => {
    mergeEmptyTableCells(tree);
    annotateTables(tree);
  };
}

function hasOnlyImageChildren(children: React.ReactNode): boolean {
  const nodes = React.Children.toArray(children).filter((child) => {
    return !(typeof child === 'string' && child.trim() === '');
  });

  return (
    nodes.length === 1 &&
    React.isValidElement(nodes[0]) &&
    (nodes[0].type === 'figure' || nodes[0].type === 'img')
  );
}

function isImageOnlyParagraphNode(node: HastNode | undefined): boolean {
  const children =
    node?.children?.filter((child) => {
      return !(child.type === 'text' && child.value?.trim() === '');
    }) ?? [];

  return children.length === 1 && children[0].tagName === 'img';
}

function extractReactText(node: React.ReactNode): string {
  if (
    typeof node === 'string' ||
    typeof node === 'number' ||
    typeof node === 'bigint'
  ) {
    return String(node);
  }
  if (Array.isArray(node)) {
    return node.map(extractReactText).join('');
  }
  if (React.isValidElement(node)) {
    return extractReactText(
      (node.props as { children?: React.ReactNode }).children,
    );
  }
  return '';
}

function normalizeCjkAdjacentBareUrls(content: string) {
  return content.replace(
    /((?:https?:\/\/|www\.)[A-Za-z0-9._~:/?#@!$&'*,;=%+-]+)([\u3400-\u9fff])/g,
    '<$1>$2',
  );
}

export interface MarkdownRendererProps {
  content: string;
  className?: string;
  components?: Components;
  allowHtml?: boolean;
  enableMath?: boolean;
  compact?: boolean;
}

export function MarkdownRenderer({
  content,
  className,
  components,
  allowHtml = false,
  enableMath = true,
  compact = false,
}: MarkdownRendererProps) {
  const copyHeadingLink = async (id: string) => {
    const url = new URL(window.location.href);
    url.hash = id;
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success('标题链接已复制');
    } catch {
      window.location.hash = id;
      toast.success('已定位到当前标题');
    }
  };

  const headingCount: Record<string, number> = {};

  const renderHeading = (
    tag: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
    children: React.ReactNode,
    className: string,
    props: React.HTMLAttributes<HTMLHeadingElement>,
  ) => {
    const id = createUniqueMarkdownHeadingId(
      extractReactText(children),
      headingCount,
    );

    return React.createElement(
      tag,
      {
        ...props,
        id,
        className: cn('group/heading scroll-mt-20 text-foreground', className),
      },
      <>
        {children}
        {id ? (
          <button
            type="button"
            aria-label="复制标题链接"
            onClick={() => void copyHeadingLink(id)}
            className="ml-2 inline-flex align-middle text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover/heading:opacity-100"
          >
            <Link2Icon className="h-4 w-4" />
          </button>
        ) : null}
      </>,
    );
  };

  const baseComponents: Components = {
    h1: ({ children, ...props }) => {
      return renderHeading(
        'h1',
        children,
        cn(
          'font-semibold tracking-tight',
          compact
            ? 'mt-4 mb-3 text-2xl leading-9'
            : 'mt-6 mb-4 text-3xl leading-tight',
        ),
        props,
      );
    },
    h2: ({ children, ...props }) => {
      return renderHeading(
        'h2',
        children,
        cn(
          'font-semibold tracking-tight',
          compact
            ? 'mt-5 mb-2.5 text-xl leading-8'
            : 'mt-7 mb-3 text-2xl leading-tight',
        ),
        props,
      );
    },
    h3: ({ children, ...props }) => {
      return renderHeading(
        'h3',
        children,
        cn(
          'font-semibold',
          compact
            ? 'mt-4 mb-2 text-lg leading-7'
            : 'mt-6 mb-2.5 text-xl leading-8',
        ),
        props,
      );
    },
    h4: ({ children, ...props }) => {
      return renderHeading(
        'h4',
        children,
        cn(
          'font-medium',
          compact
            ? 'mt-4 mb-1.5 text-base leading-6'
            : 'mt-5 mb-2 text-lg leading-7',
        ),
        props,
      );
    },
    h5: ({ children, ...props }) => {
      return renderHeading(
        'h5',
        children,
        'mt-4 mb-1.5 text-base font-medium leading-6',
        props,
      );
    },
    h6: ({ children, ...props }) => {
      return renderHeading(
        'h6',
        children,
        'mt-4 mb-1 text-sm font-medium uppercase tracking-[0.08em] text-muted-foreground',
        props,
      );
    },
    p: ({ children, node, ...props }) => {
      const className = cn(
        'break-words text-foreground/92 [overflow-wrap:anywhere]',
        compact ? 'my-2 leading-7 text-[15px]' : 'my-3 leading-8 text-base',
      );

      if (
        hasOnlyImageChildren(children) ||
        isImageOnlyParagraphNode(node as HastNode | undefined)
      ) {
        return (
          <div className={className} {...props}>
            {children}
          </div>
        );
      }

      return (
        <p className={className} {...props}>
          {children}
        </p>
      );
    },
    strong: ({ ...props }) => (
      <strong className="font-semibold text-foreground" {...props} />
    ),
    em: ({ ...props }) => <em className="italic" {...props} />,
    ul: ({ ...props }) => (
      <ul
        className={cn(
          'list-disc pl-6 text-foreground/92',
          compact ? 'my-2 space-y-1.5' : 'my-3 space-y-2',
        )}
        {...props}
      />
    ),
    ol: ({ ...props }) => (
      <ol
        className={cn(
          'list-decimal pl-6 text-foreground/92',
          compact ? 'my-2 space-y-1.5' : 'my-3 space-y-2',
        )}
        {...props}
      />
    ),
    li: ({ ...props }) => (
      <li className={cn(compact ? 'leading-7' : 'leading-8')} {...props} />
    ),
    input: ({ type, checked, disabled, ...props }) => {
      if (type !== 'checkbox') {
        return (
          <input type={type} checked={checked} disabled={disabled} {...props} />
        );
      }
      return (
        <input
          type="checkbox"
          checked={checked}
          disabled
          className="mr-2 h-4 w-4 rounded border-border align-middle accent-primary"
          {...props}
        />
      );
    },
    a: ({ href, children, ...props }) => (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="font-medium text-primary underline-offset-4 hover:underline"
        {...props}
      >
        {children}
      </a>
    ),
    hr: ({ ...props }) => <hr className="my-6 border-border/70" {...props} />,
    blockquote: ({ ...props }) => (
      <blockquote
        className={cn(
          'my-4 rounded-r-xl border-l-4 border-primary/55 bg-muted/30 pr-4 italic text-muted-foreground',
          compact
            ? 'py-2 pl-3 text-[15px] leading-7'
            : 'py-3 pl-4 text-base leading-8',
        )}
        {...props}
      />
    ),
    details: ({ ...props }) => (
      <details
        className="my-4 overflow-hidden rounded-2xl border border-border/70 bg-muted/25"
        {...props}
      />
    ),
    summary: ({ ...props }) => (
      <summary
        className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-foreground marker:hidden [&::-webkit-details-marker]:hidden"
        {...props}
      />
    ),
    sup: ({ ...props }) => (
      <sup
        className="align-super text-[0.7em] text-muted-foreground"
        {...props}
      />
    ),
    code: ({ className: codeClassName, children, ...props }) => {
      const match = /language-(\w+)/.exec(codeClassName || '');
      return match && match[1] ? (
        <CodeBlock language={match[1]}>
          {String(children).replace(/\n$/, '')}
        </CodeBlock>
      ) : (
        <code
          className="rounded-lg bg-muted px-1.5 py-0.5 font-mono text-[0.92em] text-primary"
          {...props}
        >
          {children}
        </code>
      );
    },
    pre: ({ ...props }) => (
      <pre className="my-4 overflow-x-auto rounded-xl" {...props} />
    ),
    img: ({ src, alt }) => {
      if (!src) return null;
      const showCaption = shouldRenderMarkdownImageCaption(alt);

      return (
        <figure className="my-6">
          {/* markdown 图片来源不固定，保留原生 img 避免 next/image 对未知远程域名的限制 */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            alt={alt || ''}
            src={src as string}
            className="mx-auto block h-auto max-w-full rounded-2xl border border-border/60 object-contain"
          />
          {showCaption ? (
            <figcaption className="mt-2 text-center text-sm text-muted-foreground">
              {alt}
            </figcaption>
          ) : null}
        </figure>
      );
    },
    table: ({ ...props }) => {
      const { ['data-col-count']: rawColCount, ...tableProps } =
        props as typeof props & {
          'data-col-count'?: number | string;
        };
      const colCount =
        typeof rawColCount === 'number'
          ? rawColCount
          : Number(rawColCount ?? 0);
      const tableLayoutClass =
        colCount <= 3
          ? 'min-w-full table-auto'
          : colCount <= 5
            ? 'min-w-[760px] table-auto'
            : 'min-w-[1120px] table-fixed';

      return (
        <div className="my-5 overflow-x-auto rounded-2xl border border-border/70 bg-background">
          <table
            className={cn(
              'w-full border-separate border-spacing-0 text-left',
              tableLayoutClass,
              compact ? 'text-[15px] leading-6' : 'text-base leading-7',
              '[&_p]:my-0 [&_ul]:my-0 [&_ol]:my-0 [&_li]:leading-inherit',
            )}
            {...tableProps}
          />
        </div>
      );
    },
    thead: ({ ...props }) => <thead className="bg-muted/35" {...props} />,
    tbody: ({ ...props }) => (
      <tbody className="[&_tr:nth-child(even)]:bg-muted/[0.16]" {...props} />
    ),
    th: ({ ...props }) => (
      <th
        className={cn(
          'align-middle border-b border-r border-border/70 px-4 py-3 font-semibold text-foreground/90 whitespace-normal break-words [word-break:break-word] last:border-r-0',
          compact ? 'text-sm leading-5' : 'text-[15px] leading-5',
        )}
        {...props}
      />
    ),
    td: ({ ...props }) => (
      <td
        className={cn(
          'align-middle border-b border-r border-border/50 px-4 py-3 whitespace-normal break-words text-foreground [word-break:break-word] last:border-r-0',
          compact ? 'leading-6' : 'leading-7',
        )}
        {...props}
      />
    ),
  };

  return (
    <div
      className={cn(
        'min-w-0 text-foreground',
        '[&_.katex]:inline-block [&_.katex]:max-w-full [&_.katex]:overflow-x-auto [&_.katex]:overflow-y-hidden [&_.katex]:align-middle',
        '[&_.katex-display]:max-w-full [&_.katex-display]:overflow-x-auto [&_.katex-display]:overflow-y-hidden [&_.katex-display]:py-1',
        '[&_.katex-display>.katex]:min-w-max',
        compact ? 'text-[15px]' : 'text-base',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={
          enableMath
            ? [remarkGfm, remarkMath, remarkCjkFriendly]
            : [remarkGfm, remarkCjkFriendly]
        }
        rehypePlugins={
          allowHtml
            ? enableMath
              ? [rehypeRaw, rehypeMergeEmptyTableCells, rehypeKatex]
              : [rehypeRaw, rehypeMergeEmptyTableCells]
            : enableMath
              ? [rehypeMergeEmptyTableCells, rehypeKatex]
              : [rehypeMergeEmptyTableCells]
        }
        components={{ ...baseComponents, ...components }}
      >
        {normalizeCjkAdjacentBareUrls(
          enableMath ? normalizeMarkdownMath(content) : content,
        )}
      </ReactMarkdown>
    </div>
  );
}
